// recovery-ticket-materializer.ts
//
// Orchestrator recovery (3.40.0, D2.3/D2.5/D2.7/D2.8): for every stop still
// standing in the JOURNAL with no applied/skipped recovery op for its seq,
// run the guards and either journal a skip or write the recovery ticket —
// up to 3 tries (gốc, `-r1`, `-r2`) inside THIS call, then skip
// `materialize_failed`. The same call serves the end of the attempt
// (finalize, same tick) and the tick-start scan (crash recovery), so both
// paths derive the trigger seq from the journaled `attempt_finished`. An
// intent whose stop no longer stands is settled first (recovery-orphan-intent-settle.ts).
// Off (`enabled=false` or cap 0) ⇒ reads nothing, writes nothing.
import type { StopAttemptRecord } from "./attempt-finished-journal-reader.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import {
  isOrchestratorRecoveryActive,
  readOrchestratorRecoveryConfig,
} from "./orchestrator-recovery-config.js";
import {
  evaluateRecoveryGuards,
  MAX_RECOVERY_TRIES_PER_TRIGGER,
  type RecoveryOpRecord,
  type RecoverySkipReason,
} from "./recovery-guards.js";
import {
  findPendingRecoveryTriggers,
  readRecoveryJournal,
} from "./recovery-ledger-from-journal.js";
import { commitRecoverySkip } from "./recovery-op-journal.js";
import { settleOrphanRecoveryIntents } from "./recovery-orphan-intent-settle.js";
import { buildRecoveryTicketBrief } from "./recovery-ticket-brief-builder.js";
import { tryMaterializeRecoveryTicket } from "./recovery-ticket-materialize-try.js";
import type { RecoveryMaterializeContext } from "./recovery-ticket-materializer-types.js";
import type { RecoveryTicketTrigger } from "./recovery-ticket-shape.js";
import { classifyRecoveryTrigger } from "./recovery-trigger-classifier.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { readAttemptStateFile, retryStateFor } from "./supervisor-attempt-state.js";
import { loadTicketsAtHead } from "./ticket-files-at-head.js";

export type { RecoveryMaterializeContext } from "./recovery-ticket-materializer-types.js";

export interface RecoveryMaterializeOutcome {
  source_ticket: string;
  trigger_seq: number;
  recovery_ticket?: string;
  recovery_skipped_reason?: RecoverySkipReason;
}

async function materializeChain(
  ctx: RecoveryMaterializeContext,
  stop: StopAttemptRecord,
  trigger: RecoveryTicketTrigger,
  outcome: string,
  start: { attempt: number; resume?: RecoveryOpRecord; priorTicketId?: string },
): Promise<{ recovery_ticket?: string; recovery_skipped_reason?: RecoverySkipReason }> {
  const ref = { source_ticket: stop.ticket_id, trigger, trigger_seq: stop.seq };
  let prior = start.priorTicketId;
  // Loop counter, not just journaled `failed` intents: a throw before the intent still counts (FN12).
  for (let attempt = start.attempt; attempt < MAX_RECOVERY_TRIES_PER_TRIGGER; attempt += 1) {
    const result = await tryMaterializeRecoveryTicket(ctx, {
      ref,
      attempt,
      ...(attempt === start.attempt && start.resume !== undefined ? { resume: start.resume } : {}),
      ...(prior === undefined ? {} : { priorTicketId: prior }),
      outcome,
    });
    if (result.status === "applied") {
      ctx.notify?.(`Engine tự gỡ kẹt ${stop.ticket_id} (${trigger}) ⇒ ticket ${result.ticketId}`);
      return { recovery_ticket: result.ticketId };
    }
    prior = result.reuseTicketId;
  }
  await commitRecoverySkip(ctx.store, ref, "materialize_failed");
  return { recovery_skipped_reason: "materialize_failed" };
}

function briefFor(
  stop: StopAttemptRecord,
  trigger: RecoveryTicketTrigger,
  source: Ticket,
  tickets: readonly Ticket[],
  counters: { counted_failures: number; timeout_count: number },
  secretValues: readonly string[],
  ticketStatus: Readonly<Record<string, string>>,
): string {
  const status = (id: string) => ticketStatus[id] ?? "todo";
  return buildRecoveryTicketBrief({
    trigger,
    source,
    dependents: tickets
      .filter((t) => t.depends_on.includes(source.id))
      .map((t) => ({ id: t.id, kind: t.kind, status: status(t.id) })),
    upstream: source.depends_on.map((id) => ({ id, status: status(id) })),
    counters,
    ...(stop.last_reason === undefined ? {} : { lastReason: stop.last_reason }),
    secretValues,
  });
}

export async function materializePendingRecoveries(
  ctx: RecoveryMaterializeContext,
): Promise<RecoveryMaterializeOutcome[]> {
  const config = await readOrchestratorRecoveryConfig(ctx.controlRoot);
  if (!isOrchestratorRecoveryActive(config)) return [];
  const first = await readRecoveryJournal(ctx.controlRoot);
  await settleOrphanRecoveryIntents(ctx, first);
  const pending = findPendingRecoveryTriggers(first.scan, first.ledger);
  if (pending.length === 0) return [];

  const tickets = await loadTicketsAtHead(ctx.gitRunner, ctx.repoDir);
  const attemptState = await readAttemptStateFile(ctx.controlRoot);
  const secretValues = await knownValuesForControlRoot(ctx.controlRoot);
  const outcomes: RecoveryMaterializeOutcome[] = [];
  for (const stop of pending) {
    const source = tickets.find((t) => t.id === stop.ticket_id);
    const verdict = classifyRecoveryTrigger({
      kind: source?.kind ?? "unknown",
      blocked_by: stop.blocked_by,
      category: stop.category,
    });
    // Re-read per trigger: a ticket applied just above counts toward cap / repeat.
    const { ledger } = await readRecoveryJournal(ctx.controlRoot);
    const guard = evaluateRecoveryGuards({
      active: true,
      maxRecoveries: config.max_recoveries_per_mission,
      verdict,
      triggerSeq: stop.seq,
      ledger,
    });
    if (verdict.trigger === "other") continue;
    const trigger = verdict.trigger;
    const head = { source_ticket: stop.ticket_id, trigger_seq: stop.seq };
    if (guard.action === "skip") {
      await commitRecoverySkip(ctx.store, { ...head, trigger }, guard.reason);
      outcomes.push({ ...head, recovery_skipped_reason: guard.reason });
      continue;
    }
    if ((guard.action !== "create" && guard.action !== "resume") || source === undefined) continue;
    const retry = retryStateFor(attemptState, source.id);
    const outcome = briefFor(
      stop,
      trigger,
      source,
      tickets,
      retry,
      secretValues,
      ctx.store.state.tickets,
    );
    const start =
      guard.action === "resume"
        ? { attempt: guard.attempt, resume: guard.pending }
        : {
            attempt: guard.attempt,
            ...(guard.priorTicketId === undefined ? {} : { priorTicketId: guard.priorTicketId }),
          };
    outcomes.push({ ...head, ...(await materializeChain(ctx, stop, trigger, outcome, start)) });
  }
  return outcomes;
}
